"use client";

import {
  ACCESS_TOKEN_STORAGE_KEY,
  getApiAccessToken,
  markAccountSwitched,
  setApiAccessToken,
  type AccountSwitchInfo,
} from "@/lib/api-client";
import { decodeJwtPayload } from "@/lib/jwt";

/**
 * Watches the shared localStorage token for a change made by ANOTHER tab of
 * this origin. Chrome fires `storage` only in the tabs that did not write, so
 * this never reacts to the tab's own login/refresh/logout.
 *
 * Three outcomes:
 *  - same subject, new token: another tab of the same account refreshed. Adopt
 *    it silently so both tabs carry the newest token (source "sync", no cookie
 *    PATCH — the refreshing tab already did that).
 *  - different subject, or token removed: the browser session now belongs to
 *    someone else (or nobody). Freeze this tab via markAccountSwitched; the
 *    SessionProvider shows the reload dialog.
 *  - this tab holds no token (login page, signed-out view): nothing to protect.
 */
export function watchForCrossTabAccountSwitch(): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== ACCESS_TOKEN_STORAGE_KEY) {
      return;
    }
    const current = getApiAccessToken();
    if (!current) {
      return;
    }

    // key === null means localStorage.clear() ran somewhere: treat like a sign-out.
    const next = event.key === null ? null : event.newValue;
    if (!next) {
      markAccountSwitched({ subject: null });
      return;
    }
    if (next === current) {
      return;
    }

    const currentSubject = readSubject(current);
    const nextSubject = readSubject(next);
    if (nextSubject && currentSubject && nextSubject === currentSubject) {
      setApiAccessToken(next, { source: "sync" });
      return;
    }

    markAccountSwitched(describeToken(next));
  };

  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("storage", onStorage);
  };
}

function readSubject(token: string): string | null {
  const sub = decodeJwtPayload(token)?.sub;
  return typeof sub === "string" && sub.length > 0 ? sub : null;
}

function describeToken(token: string): AccountSwitchInfo {
  const payload = decodeJwtPayload(token);
  const email = payload?.email;
  return {
    subject: readSubject(token) ?? "unknown",
    email: typeof email === "string" ? email : null,
  };
}
